import { useNavigate } from "react-router-dom";
import {
  appIsAuthenticatedSelector,
  clearEnterPinErrorCount,
  enterPinErrorCountSelector,
  incrementEnterPinErrorCount,
  loginErrorSelector,
  logout,
  refreshTokenSelector,
  runtimeTokenSelector,
  selectAuthStatus,
  setAppIsAuthenticated,
  setAuthStatus,
  setEnterPinError,
  setLoginError,
  setRefreshToken,
  setRuntimeToken,
  setShouldEnterPin,
  setToken,
} from "@/services/auth";
import {
  AuthStatus,
  useLazyGetStateQuery,
  useLazyRuntimeTokenQuery,
  useLoginMutation,
  useLogoutMutation,
} from "@/services/api";
import { useAppDispatch, useAppSelector } from "@/services/store";
import { appRouterPaths } from "@/services/navigation";
import { useAccessPinCodeMutation } from "@/services/api/entities/pinCode";
import { signUpActions } from "@/features/sign-up";

export const useAuth = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const refreshToken = useAppSelector(refreshTokenSelector);
  const runtimeToken = useAppSelector(runtimeTokenSelector);
  const appIsAuthenticated = useAppSelector(appIsAuthenticatedSelector);
  const authStatus = useAppSelector(selectAuthStatus);
  const enterPinErrorCount = useAppSelector(enterPinErrorCountSelector);
  const loginError = useAppSelector(loginErrorSelector);

  const [logoutMutation] = useLogoutMutation();
  const [getRuntimeToken] = useLazyRuntimeTokenQuery();
  const [login] = useLoginMutation();
  const [getState] = useLazyGetStateQuery();
  const [accessPin] = useAccessPinCodeMutation();

  const loginHandler = ({ username, password }: { username: string; password: string }) => {
    dispatch(setLoginError(null));
    login({ username, password })
      .unwrap()
      .then((data) => {
        if (!data) return;
        dispatch(setToken(data?.token));
        dispatch(setRefreshToken(data?.refreshToken));
      })
      .then(() => {
        getState()
          .unwrap()
          .then((res) => {
            dispatch(setAuthStatus(res.state));
            navigate(appRouterPaths.checkStatus);
          })
          .catch(() => {
            dispatch(logout());
            navigate(appRouterPaths.logIn);
          });
      })
      .catch((error) => {
        dispatch(setLoginError(error.message));
      });
  };

  const accessPinHandler = (pin: string) => {
    dispatch(setEnterPinError(false));
    accessPin({ pin })
      .unwrap()
      .then(() => {
        dispatch(clearEnterPinErrorCount());

        getState()
          .unwrap()
          .then((res) => {
            dispatch(setAuthStatus(res.state));
            dispatch(setAppIsAuthenticated(true));
            dispatch(setShouldEnterPin(false));
            dispatch(signUpActions.clearState());
            navigate(appRouterPaths.dashboards);
            getRuntimeToken()
              .unwrap()
              .then((runtimeTokenRes) => {
                dispatch(setRuntimeToken(runtimeTokenRes.runtimeToken));
              })
              .catch();
          });
      })
      .catch(() => {
        dispatch(incrementEnterPinErrorCount());
        dispatch(setEnterPinError(true));

        if (enterPinErrorCount >= 2) {
          dispatch(setEnterPinError(false));
          dispatch(clearEnterPinErrorCount());
          dispatch(setAppIsAuthenticated(false));
          dispatch(setAuthStatus(AuthStatus.BLOCKED));
          navigate(appRouterPaths.checkStatus);
        }
      });
  };

  const logoutHandler = () => {
    logoutMutation({ refreshToken: refreshToken ?? "" });
    dispatch(logout());
    navigate(appRouterPaths.logIn);
    dispatch(setAppIsAuthenticated(false));
    dispatch(setAuthStatus(null));
  };

  return {
    loginHandler: loginHandler,
    logoutHandler: logoutHandler,
    isAuthenticated: appIsAuthenticated,
    accessPinHandler,
    runtimeToken: runtimeToken,
    authStatus,
    loginError,
  };
};
